import { useMemo, useState, useEffect, useCallback } from "react";

import { useTranslation } from "react-i18next";

import { Grid, Button } from "@mui/material";

import DefaultPageWrapper from "../../components/wrapper/DefaultPageWrapper";

import NavigationDrawer from "../../components/drawer/NavigationDrawer";
import LoaderWrapper from "../../components/wrapper/LoaderWrapper";

import useFilterSearchParams from "../../hooks/useFilterSearchParams";
import usePaginationWithSearchParams from "../../hooks/usePaginationWithSearchParams";

import { useAsync, useAsyncFn } from "../../hooks/useAsync";
import useEquipmentService from "../../services/equipmentService";

import UniversalToolBarWithDialogs from "../../components/bar/UniversalToolBarWithDialogs";

import { NAVIGATION_DRAWER_TYPE_METERS } from "../../components/drawer/NavigationDrawer/NavigationDrawer";

import ElectricityDistributionTable from "../../components/table/ElectricityDistributionTable";
import TableService from "../../services/tableService";
import { exportElecticityDistributionUrl } from "../../helpers/apiUrls";

import { useSnackbarAlert } from "../../context/snackbarAlert";
import ChangeElectricityDistributionAcceptanceButton from "../../components/button/ChangeElecticityDistributionAcceptanceButton/ChangeElectricityDistributionAcceptanceButton";
import ChangeElectricityDistributionApprovedByDcButton from "../../components/button/ChangeEdApprovedByDcButton/ChangeEdApprovedByDcButton";

import EquipmentDifferentialMonthParamsListDialog from "../../components/dialog/EquipmentDifferentialMonthParamsListDialog";
import ElectricityDistributionSymfoniaDialog from "../../components/dialog/ElectricityDistributionSymfoniaDialog";
import useDialog from "../../hooks/useDialog";
import {
  overflowButtonStyle,
  centerVericalAlignIconStyle,
} from "../../helpers/styles";
import SyncIcon from "@mui/icons-material/Sync";
import {
  ED_TABLE_NAME,
  ED_FILTER_PREFIX,
  ED_TABLE_FIELDS_CONFIG,
} from "./TableConfig";

import useCheckPermission from "../../hooks/usePermission";
import {
  EDIT_COUNTER_ED_PERMISSION,
  EDIT_ED_INVOICED_PERMISSION,
  EDIT_ED_APPROVED_BY_DC,
} from "../../helpers/constants";

const CONFIG_FOR_USER_PREFERENCEDIALOG = {
  name: ED_TABLE_NAME,
  config: ED_TABLE_FIELDS_CONFIG,
};

export default function ElectricityDistributionPage(props) {
  const { pageName } = props;

  const [hasPermissionToEditEd] = useCheckPermission(
    EDIT_COUNTER_ED_PERMISSION
  );

  const [hasPermissionToEditEdInvoiced] = useCheckPermission(
    EDIT_ED_INVOICED_PERMISSION
  );

  const [hasPermissionToEditEdApprovedByDc] = useCheckPermission(
    EDIT_ED_APPROVED_BY_DC
  );

  const tableConfig = useMemo(() => {
    let tableConfigTemp = ED_TABLE_FIELDS_CONFIG;

    if (!hasPermissionToEditEd) {
      tableConfigTemp = tableConfigTemp.filter(
        (config) => config.name != "recalculate"
      );
    }

    return tableConfigTemp;
  }, [hasPermissionToEditEd]);

  const [
    openSymfoniaDialog,
    handleOpenSymfoniaDialog,
    handleCloseSymfoniaDialog,
  ] = useDialog();

  const [openEdmpDialog, handleOpenEdmpDialog, onCloseEdmpDialog] = useDialog();

  const snackbarAlert = useSnackbarAlert();
  const { t } = useTranslation();

  const [dataLocaly, setDataLocaly] = useState();
  const [countRecords, setCountRecords] = useState();

  const {
    page,
    pageSize,
    handleChangePageWithSearchParams,
    handleChangeRowsPerPage,
    resetPageNumber,
    searchParams,
    setSearchParams,
  } = usePaginationWithSearchParams(ED_FILTER_PREFIX);

  const {
    setNewItemSearchParamsIfAreChanged,
    getSearchParamsByFilterPrefix,
    clearSearchParamsByFilterPrefixes,
  } = useFilterSearchParams(searchParams);

  const [filterSearchParams, setFilterSearchParams] = useState(
    getSearchParamsByFilterPrefix(ED_FILTER_PREFIX)
  );

  useEffect(() => {
    setNewItemSearchParamsIfAreChanged(
      ED_FILTER_PREFIX,
      filterSearchParams,
      setFilterSearchParams
    );
  }, [searchParams]);

  const {
    getElectricityDistributionListData,
    getElectricityDistributionFilteringData,
    recalculateElectricityDistribution,
    recalculateElectricityDistributionMass,
  } = useEquipmentService();

  const filteringData = useAsync(getElectricityDistributionFilteringData);
  const recalculateElectricityDistributionFn = useAsyncFn(
    recalculateElectricityDistribution
  );

  const recalculateElectricityDistributionMassFn = useAsyncFn(
    recalculateElectricityDistributionMass
  );

  const backendData = useAsync(
    () => getElectricityDistributionListData(filterSearchParams),
    [filterSearchParams]
  );

  useEffect(() => {
    if (backendData.loading) {
      return;
    }
    setDataLocaly(backendData.value.results);
    setCountRecords(backendData.value.count);
  }, [backendData.loading]);

  const [hiddenColumnsForTables, setHiddenColumnsForTables] = useState();
  const [columnsOrdersForTables, setColumnsOrdersForTables] = useState();

  const refreshTable = () => {
    setDataLocaly(undefined);
    handleChangePageWithSearchParams(undefined, 1);
    backendData.refetch();
  };

  const exportHeaders = useMemo(() => {
    return TableService.getTableExportHeaders(
      ED_TABLE_FIELDS_CONFIG,
      hiddenColumnsForTables?.[ED_TABLE_NAME],
      columnsOrdersForTables?.[ED_TABLE_NAME]
    );
  }, [
    ED_TABLE_FIELDS_CONFIG,
    hiddenColumnsForTables?.[ED_TABLE_NAME],
    columnsOrdersForTables?.[ED_TABLE_NAME],
  ]);

  const handleRecalculateElectricityDistribution = (
    equipmentId,
    year,
    month,
    eits_usage,
    equipment_scheme
  ) => {
    recalculateElectricityDistributionFn
      .execute(
        equipmentId,
        year,
        month,
        year,
        month,
        eits_usage,
        equipment_scheme
      )
      .then((res) => {
        snackbarAlert.openSuccessSnackbarAlert(
          t("snackbar_alert.electricity_distribution_updated")
        );
        refreshTable();
      })
      .catch((error) => {
        snackbarAlert.openErrorSnackbarAlert(
          t(
            "snackbar_alert.occurred_error_during_updating_electricity_distribution"
          )
        );
      });
  };

  const handleRecalculateElectricityDistributionMass = () => {
    if (!getSeletedEdIds()) return;

    recalculateElectricityDistributionMassFn
      .execute(getSeletedEdIds())
      .then((res) => {
        snackbarAlert.openSuccessSnackbarAlert(
          t("snackbar_alert.electricity_distribution_updated")
        );
        refreshTable();
      })
      .catch((error) => {
        snackbarAlert.openErrorSnackbarAlert(
          t(
            "snackbar_alert.occurred_error_during_updating_electricity_distribution"
          )
        );
      });
  };

  const handleClickCheck = useCallback((id) => {
    setDataLocaly((prevData) => {
      let selectedRow = { ...prevData.find((rowData) => rowData.id === id) };
      selectedRow.selected = !selectedRow.selected;
      return prevData.map((rowData) => {
        if (rowData.id === id) return selectedRow;
        return rowData;
      });
    });
  }, []);

  const [checkedAll, setCheckedAll] = useState(false);
  const handleClickCheckAll = useCallback(
    (value) => {
      let currValue = !checkedAll;
      setCheckedAll(currValue);

      setDataLocaly((prev) => {
        return prev.map((rowData) => ({
          ...rowData,
          selected: currValue,
        }));
      });
    },
    [checkedAll]
  );

  const getSeletedEdIds = () =>
    dataLocaly
      .filter((equipment) => equipment.selected)
      .map((equipment) => equipment.id);

  const handleCloseEdmpDialog = (existChanges) => {
    if (existChanges) {
      refreshTable();
    }
    onCloseEdmpDialog();
  };

  const isLoading =
    dataLocaly === undefined ||
    filteringData.loading ||
    recalculateElectricityDistributionFn.loading ||
    recalculateElectricityDistributionMassFn.loading;

  if (isLoading) return <LoaderWrapper showLoader={true} />;

  return (
    <NavigationDrawer
      pageName={pageName}
      drawerType={NAVIGATION_DRAWER_TYPE_METERS}
    >
      <DefaultPageWrapper titleKey={"electricity_distribution"}>
        <Grid
          container
          item
          direction="row"
          justifyContent="flex-start"
          alignItems="center"
          rowSpacing={1}
        >
          <Grid item xs={12}>
            <UniversalToolBarWithDialogs
              pageName={props.pageName}
              tableConfig={CONFIG_FOR_USER_PREFERENCEDIALOG}
              filteringData={filteringData}
              refreshTable={refreshTable}
              onCleanFlitersInFilterDialog={() => {}}
              resetPageNumber={resetPageNumber}
              hiddenColumnsForTables={hiddenColumnsForTables}
              setHiddenColumnsForTables={setHiddenColumnsForTables}
              columnsOrdersForTables={columnsOrdersForTables}
              setColumnsOrdersForTables={setColumnsOrdersForTables}
              showOpenCloseFilterButton={false}
              showCreateButton={true}
              showCleanfiltersButton={false}
              onClickCreateItem={handleOpenEdmpDialog}
              createButtonLabel={t("dialog.equipment.edpm")}
              extraButtonList={[
                <Button
                  fullWidth
                  variant="contained"
                  size="small"
                  onClick={handleOpenSymfoniaDialog}
                  sx={overflowButtonStyle}
                >
                  {t(
                    "dialog.equipment.electricity_distribution_symfonia_report"
                  )}
                </Button>,
                hasPermissionToEditEdInvoiced ? (
                  <ChangeElectricityDistributionAcceptanceButton
                    selectedElectricityDistributionIds={getSeletedEdIds()}
                    onSubmit={refreshTable}
                  />
                ) : null,
                hasPermissionToEditEdApprovedByDc ? (
                  <ChangeElectricityDistributionApprovedByDcButton
                    selectedElectricityDistributionIds={getSeletedEdIds()}
                    onSubmit={refreshTable}
                  />
                ) : null,
                hasPermissionToEditEd ? (
                  <Button
                    startIcon={<SyncIcon sx={centerVericalAlignIconStyle} />}
                    fullWidth
                    variant="contained"
                    size="small"
                    onClick={handleRecalculateElectricityDistributionMass}
                    sx={overflowButtonStyle}
                    disabled={getSeletedEdIds()?.length === 0}
                  >
                    {getSeletedEdIds()?.length === 0
                      ? t("select_rows_to_recalculate")
                      : t("recalculate_all")}
                  </Button>
                ) : null,
              ]}
            />
          </Grid>
          <Grid item xs={12}>
            <LoaderWrapper showLoader={isLoading}>
              {hiddenColumnsForTables && columnsOrdersForTables && (
                <ElectricityDistributionTable
                  showCheckbox={true}
                  checkedAll={checkedAll}
                  onClickCheckAll={handleClickCheckAll}
                  onClickCheck={handleClickCheck}
                  data={dataLocaly}
                  showCleanFilterIcon={true}
                  countRecords={countRecords}
                  page={page}
                  onPageChange={handleChangePageWithSearchParams}
                  rowsPerPage={pageSize}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  resetPageNumber={resetPageNumber}
                  filteringData={filteringData}
                  filterPrefix={ED_FILTER_PREFIX}
                  style={{ maxHeight: "75vh" }}
                  tableConfig={tableConfig}
                  hiddenColumns={hiddenColumnsForTables[ED_TABLE_NAME]}
                  columnsOrders={columnsOrdersForTables[ED_TABLE_NAME]}
                  onClickRefresh={handleRecalculateElectricityDistribution}
                  showExportToFileButton={true}
                  exportToFileUrl={exportElecticityDistributionUrl}
                  exportToFileSearchParams={filterSearchParams}
                  exportToFileHeaders={exportHeaders}
                  exportToFileFileName={`${t(
                    "headers.electricity_distribution"
                  )}.xlsx`}
                  isLoading={backendData.loading}
                  showTableLoader={true}
                />
              )}
            </LoaderWrapper>
            {openSymfoniaDialog && (
              <ElectricityDistributionSymfoniaDialog
                open={openSymfoniaDialog}
                onClose={handleCloseSymfoniaDialog}
              />
            )}
            {openEdmpDialog && (
              <EquipmentDifferentialMonthParamsListDialog
                open={openEdmpDialog}
                onClose={handleCloseEdmpDialog}
              />
            )}
          </Grid>
        </Grid>
      </DefaultPageWrapper>
    </NavigationDrawer>
  );
}
